import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import http from '../http-common';
import {
  buildAppTheme,
  DEFAULT_THEME_CONFIG,
  normalizeThemeConfig,
  themeConfigToSettings,
} from '../theme/buildAppTheme';

const AppThemeContext = createContext(null);

export const useAppTheme = () => {
  const context = useContext(AppThemeContext);
  if (!context) {
    throw new Error('useAppTheme must be used within AppThemeProvider');
  }
  return context;
};

export const AppThemeProvider = ({ children }) => {
  const [themeConfig, setThemeConfig] = useState(DEFAULT_THEME_CONFIG);
  const [ready, setReady] = useState(false);

  const loadTheme = useCallback(async () => {
    try {
      const response = await http.get('/settings');
      setThemeConfig(normalizeThemeConfig(response.data?.settings || {}));
    } catch (error) {
      console.error('Error loading theme settings:', error);
      setThemeConfig(DEFAULT_THEME_CONFIG);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    loadTheme();
  }, [loadTheme]);

  const saveThemeConfig = useCallback(async (nextConfig) => {
    const payload = themeConfigToSettings(nextConfig);
    await http.put('/settings', payload);
    setThemeConfig(nextConfig);
  }, []);

  const muiTheme = useMemo(() => buildAppTheme(themeConfig), [themeConfig]);

  const value = useMemo(
    () => ({
      themeConfig,
      setThemeConfig,
      reloadTheme: loadTheme,
      saveThemeConfig,
    }),
    [themeConfig, loadTheme, saveThemeConfig]
  );

  if (!ready) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AppThemeContext.Provider value={value}>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </AppThemeContext.Provider>
  );
};
